/**
 * Workflows compound tool — list, checkpoint, get-run.
 * Consolidates workflow discovery, checkpoint persistence, and run inspection.
 * Chain: FEAT-MCP-001 (MCP Server), BET-85
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import {
  getAgentSessionId,
  kernelQuery,
  kernelMutation,
  requireActiveSession,
  runWithToolContext,
  requireWriteAccess,
} from "../client.js";
import { resolveCollection } from "../lib/resolveCollection.js";
import { thinWrapper, success, failure, validationResult, successResult, parseOrFail, textContent, type NextAction, type ToolResult } from "../envelope.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { loadSkillBody } from "./skills.js";
import {
  getWorkflowDescriptor,
  getWorkflow,
  listWorkflows,
  type WorkflowDefinition,
} from "../workflows/definitions.js";
import {
  describeWorkflowPrimaryOutput,
  getWorkflowSummaryCaptureDefinition,
  getWorkflowTemplateMetadata,
} from "../workflows/descriptor.js";
import type {
  JsonValue,
  WorkflowRunOutput,
  WorkflowRunRenderProjection,
} from "../workflows/run-model.js";
import { workflowRunOutputToText } from "../workflows/run-model.js";

// WP-484 S2 (§3): `workflows` absorbs `skills` → load-skill. Legacy `skills`
// tool stays registered alongside (§7 "alongside" mechanic) — both call the
// SAME loadSkillBody function, one business-logic path.
const WORKFLOWS_ACTIONS = ["list", "start", "checkpoint", "get-run", "load-skill"] as const;
type WorkflowsAction = (typeof WORKFLOWS_ACTIONS)[number];
type WorkflowCheckpointOutputInput = string | WorkflowRunOutput;

const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

const workflowRunOutputInputSchema: z.ZodType<WorkflowRunOutput> = z.union([
  z.object({
    format: z.literal("freetext"),
    value: z.string(),
  }),
  z.object({
    format: z.literal("list"),
    value: z.array(z.string()),
  }),
  z.object({
    format: z.literal("choice"),
    value: z.union([z.string(), z.array(z.string())]),
  }),
  z.object({
    format: z.literal("structured"),
    value: jsonValueSchema,
  }),
]);

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const workflowsSchema = z.object({
  action: z.enum(WORKFLOWS_ACTIONS).describe(
    "'list': browse available workflows. 'start': start or resume a workflow — returns first (or current) round and next-step checkpoint call. 'checkpoint': record round output or final summary. 'get-run': inspect a persisted workflow run. " +
      "'load-skill': load the markdown body of a SKILL-* entry from the workspace (absorbs the `skills` tool). Requires entryId.",
  ),
  workflowId: z.string().max(200).optional().describe("Workflow ID for start, checkpoint, or get-run, e.g. 'retro', 'implementation-review'"),
  runId: z.string().max(200).optional().describe("Workflow run ID: for get-run, which run to load; for checkpoint, target this run (avoids session drift — pass runId from get-run)."),
  roundId: z.string().max(200).optional().describe("Round ID for checkpoint, e.g. 'what-went-well'"),
  output: z.union([z.string(), workflowRunOutputInputSchema]).optional().describe("The round's output — either legacy synthesized text or a typed workflow run payload."),
  isFinal: z.boolean().optional().describe("If true, finalize an existing durable workflow run from its terminal round and create the summary chain entry."),
  restart: z.boolean().optional().describe("If true, start a new durable run from the workflow's first round in the current session."),
  summaryName: z.string().max(500).optional().describe("Optional name for final chain entry. If omitted, the workflow summary template is used."),
  summaryDescription: z.string().max(20000).optional().describe("Optional override for the final chain entry description. Defaults to the final round output text."),
  summaryEntryId: z.string().max(200).optional().describe("Link an existing entry as the run's summary instead of creating one. Used by facilitated workflows (e.g. shape) where the primary record is created by the specialized tool."),
  entryId: z.string().max(200).optional().describe("For 'load-skill': workspace-scoped skill entry id (e.g. 'SKILL-pb-setup'). Refuses non-skill entries (canonicalKey !== 'skill') with INVALID_KIND."),
  // WP-513: team+role to create the finalize summary AS OWNER of (rung 2 only).
  ownerTeamEntryId: z.string().max(200).optional().describe("For 'checkpoint' (isFinal): owning team (rung-2)."),
  ownerRoleEntryId: z.string().max(200).optional().describe("For 'checkpoint' (isFinal): owning role (rung-2)."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion.
const workflowsListVariant = z.object({ action: z.literal("list") });
// Note: "runId OR workflowId required" is a cross-field check the
// discriminatedUnion can't express (z.discriminatedUnion members must be
// plain ZodObjects, not ZodEffects) — enforced in the handler instead,
// mirroring context.ts's entryId-or-task check for 'gather'.
const workflowsGetRunVariant = z.object({
  action: z.literal("get-run"),
  runId: z.string().max(200).optional(),
  workflowId: z.string().max(200).optional(),
});
const workflowsStartVariant = z.object({ action: z.literal("start"), workflowId: z.string().max(200) });
const workflowsCheckpointVariant = z.object({
  action: z.literal("checkpoint"),
  workflowId: z.string().max(200),
  roundId: z.string().max(200),
  output: z.union([z.string(), workflowRunOutputInputSchema]),
  isFinal: z.boolean().optional(),
  restart: z.boolean().optional(),
  summaryName: z.string().max(500).optional(),
  summaryDescription: z.string().max(20000).optional(),
  summaryEntryId: z.string().max(200).optional(),
  runId: z.string().max(200).optional(),
  ownerTeamEntryId: z.string().max(200).optional(), ownerRoleEntryId: z.string().max(200).optional(),
});
const workflowsLoadSkillVariant = z.object({ action: z.literal("load-skill"), entryId: z.string().max(200).min(1) });

export const workflowsActionUnion = z.discriminatedUnion("action", [
  workflowsListVariant,
  workflowsGetRunVariant,
  workflowsStartVariant,
  workflowsCheckpointVariant,
  workflowsLoadSkillVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const WORKFLOWS_ACTION_SPECS: Record<WorkflowsAction, ActionParamSpec> = {
  list: { params: [], description: "Browse available workflows; no params." },
  "get-run": { params: ["runId", "workflowId"], description: "runId or workflowId is required." },
  start: { params: ["workflowId"], description: "workflowId is required." },
  checkpoint: { params: ["workflowId", "roundId", "output", "isFinal", "restart", "summaryName", "summaryDescription", "summaryEntryId", "runId", "ownerTeamEntryId", "ownerRoleEntryId"], description: "workflowId, roundId, and output are required." },
  "load-skill": { params: ["entryId"], description: "entryId is required." },
};

function formatWorkflowCard(wf: WorkflowDefinition): string {
  const roundList = wf.rounds
    .map((r) => `  ${r.num}. ${r.label} (${r.type}, ~${r.maxDurationHint ?? "?"})`)
    .join("\n");
  const descriptor = getWorkflowDescriptor(wf.id);
  const outputDescription = descriptor
    ? describeWorkflowPrimaryOutput(descriptor)
    : wf.kbOutputCollection
      ? `Primary record routes to \`${wf.kbOutputCollection}\`.`
      : "Primary output routing is resolved at runtime.";

  return (
    `## ${wf.icon} ${wf.name}\n` +
    `**ID**: \`${wf.id}\`\n` +
    `${wf.shortDescription}\n\n` +
    `**Rounds** (${wf.rounds.length}):\n${roundList}\n\n` +
    `**Output**: ${outputDescription}\n` +
    `_Trigger via Facilitator Mode: say "run retro", "shape this", "review implementation", or "process change" in Cursor._`
  );
}

/**
 * Thin-wrapper contract for MCP workflow tools (TEN-667).
 *
 * MCP handles ONLY:
 *   1. Workflow definition lookup — definitions are MCP-local code (packages/mcp-server/src/workflows/)
 *   2. Round validation against the definition — requires local definition access
 *   3. Argument translation — coerce MCP text/string inputs to typed WorkflowRunOutput
 *   4. Collection pre-resolution — classifier mode requires an LLM action (cannot run in a mutation)
 *   5. Response formatting — MCP tool protocol envelope
 *
 * Everything else lives in Convex:
 *   - Run state machine (create, advance, complete): lib/workflowRun.ts
 *   - Persistence: chainwork/engine.ts (internal mutations, MCP gateway path)
 *   - Public write surface for Brain/non-MCP consumers: chainwork/mutations.ts (TEN-667)
 *   - Auth: requirePerson (public) or requireActiveWorkflowSession (internal/MCP)
 *
 * Do NOT add state machine logic, run lifecycle checks, or persistence here.
 */

export function validateFinalWorkflowCheckpoint(
  workflow: WorkflowDefinition,
  roundId: string,
): string | null {
  const finalRound = workflow.rounds.at(-1);

  if (!finalRound) {
    return `Workflow '${workflow.id}' has no rounds and cannot create a final summary.`;
  }

  if (roundId !== finalRound.id) {
    return (
      `Workflow '${workflow.id}' can only create a final summary from its terminal round ` +
      `'${finalRound.id}' (${finalRound.label}).`
    );
  }

  return null;
}

// ─── Closest-match helpers (used for round ID self-correction) ──────────────
// BET-78 (response envelope): errors must carry next-step commands agents can
// execute directly. INS-61: design for the dumbest agent — if a round ID typo
// causes a silent "not found", the agent retries blindly. Suggesting the
// closest round turns a dead-end into a one-shot recovery.

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j]!, dp[i][j - 1]!, dp[i - 1][j - 1]!);
    }
  }
  return dp[m][n]!;
}

/**
 * Returns the closest matching round for a given input ID, or null if no round
 * is similar enough to confidently suggest. Prioritises substring containment
 * over edit distance so "orient-scope" → "orient" works without needing a low
 * edit-distance threshold.
 */
function findClosestRound(
  input: string,
  rounds: WorkflowDefinition["rounds"],
): WorkflowDefinition["rounds"][number] | null {
  if (rounds.length === 0) return null;

  // Prefer substring containment (either direction) — catches truncations and
  // common over-hyphenation mistakes before paying for edit-distance.
  const substringMatch = rounds.find(
    (r) => r.id.includes(input) || input.includes(r.id),
  );
  if (substringMatch) return substringMatch;

  // Fall back to edit distance. Only suggest if distance ≤ half the longer
  // string — avoids wild guesses on completely unrelated IDs.
  let best = rounds[0]!;
  let bestDist = levenshtein(input, best.id);
  for (const r of rounds.slice(1)) {
    const dist = levenshtein(input, r.id);
    if (dist < bestDist) {
      bestDist = dist;
      best = r;
    }
  }

  const threshold = Math.ceil(Math.max(input.length, best.id.length) / 2);
  return bestDist <= threshold ? best : null;
}

export function registerWorkflowTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const workflowsHandlers: Record<WorkflowsAction, (data: any) => Promise<ToolResult>> = {
    list: () => handleList(),
    "get-run": (data) => {
      if (!data.runId && !data.workflowId) {
        return Promise.resolve(validationResult("runId or workflowId is required when action is 'get-run'."));
      }
      return handleGetRun(data.runId, data.workflowId);
    },
    start: (data) => handleStart(data.workflowId),
    checkpoint: (data) => handleCheckpoint(
      data.workflowId, data.roundId, data.output, data.isFinal, data.restart,
      data.summaryName, data.summaryDescription, data.summaryEntryId, data.runId,
      data.ownerTeamEntryId, data.ownerRoleEntryId,
    ),
    "load-skill": async (data) => {
      const result = await loadSkillBody(data.entryId);
      const summary = `Loaded ${result.entryId} (${result.body.length} chars).`;
      return { content: textContent(result.body), structuredContent: success(summary, result) };
    },
  };

  server.registerTool(
    "workflows",
    {
      title: "Workflows",
      description:
        "Chainwork workflow discovery, start/resume, checkpointing, run inspection, and skill loading. Five actions:\n\n" +
        "- **list**: Browse available workflows — retro, shape-a-bet, implementation-review, process-change.\n" +
        "- **start**: Start or resume a workflow. Call with workflowId (e.g. 'implementation-review', 'retro', 'shape', 'process-change'). " +
        "Returns the first (or current) round instructions and the exact checkpoint call to use when the round is complete. " +
        "Use this when the user says 'run implementation review', 'run retro', 'shape this', or 'process change' — it is the tool entry point for running a workflow.\n" +
        "- **checkpoint**: Record round output during Facilitator Mode. Persists progress. " +
        "At completion, some workflows support isFinal=true to create the summary chain entry.\n" +
        "- **get-run**: Inspect a persisted workflow run by run ID or by workflow ID in the current session.\n" +
        "- **load-skill**: Load the markdown body of a SKILL-* entry from the current workspace (absorbs `skills`). " +
        "Generic loader — works for any skill, e.g. SKILL-pb-setup, SKILL-shape, SKILL-review.",
      inputSchema: workflowsSchema,
      annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(workflowsSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "workflows", action }, () =>
        dispatchDiscriminated("workflows", workflowsActionUnion, parsed.data, WORKFLOWS_ACTION_SPECS, workflowsHandlers),
      );
    }),
  );
}

async function handleList() {
  const workflows = listWorkflows();

  if (workflows.length === 0) {
    return successResult(
      "No workflows registered yet. Check back after the workflow definitions are configured.",
      "No workflows registered yet.",
      { workflows: [], total: 0 },
    );
  }

  const cards = workflows.map(formatWorkflowCard).join("\n\n---\n\n");

  return {
    content: [{
      type: "text" as const,
      text:
        `# Available Chainwork Workflows\n\n` +
        `${workflows.length} workflow(s) available. Trigger via Facilitator Mode in Cursor (say "run retro", "shape this", "review implementation", or "process change").\n\n` +
        `---\n\n${cards}`,
    }],
    structuredContent: success(
      `Found ${workflows.length} workflow(s).`,
      {
        workflows: workflows.map((w) => ({
          id: w.id,
          name: w.name,
          rounds: w.rounds.length,
          outputCollection: w.kbOutputCollection ?? null,
        })),
        total: workflows.length,
      },
    ),
  };
}

/** TEN-244: Bridge for "run workflow" — start returns first (or current) round so agents have a tool entry point. */
export async function handleStart(workflowId: string) {
  requireActiveSession();
  const agentSessionId = getAgentSessionId();
  if (!agentSessionId) {
    return validationResult(
      "Active agent session required to start a workflow. Ensure the client has an active session.",
    );
  }

  const wf = getWorkflow(workflowId);
  if (!wf) {
    const available = listWorkflows().map((w) => w.id).join(", ");
    return {
      content: [{
        type: "text" as const,
        text:
          `Workflow "${workflowId}" not found. Available: ${available}.\n\n` +
          `Use workflows action=list to see workflow names and round structure.`,
      }],
      structuredContent: failure(
        "NOT_FOUND",
        `Workflow '${workflowId}' not found.`,
        `Available: ${available}`,
        [{ tool: "workflows", description: "List workflows", parameters: { action: "list" } }],
      ),
    };
  }

  const firstRound = wf.rounds[0];
  if (!firstRound) {
    return validationResult(
      `Workflow '${workflowId}' has no rounds. Use workflows action=list to choose another workflow.`,
    );
  }

  // Resume: if there is an active run for this session+workflow, return current round and runId
  let run: PersistedWorkflowRun | null = null;
  try {
    run = await kernelQuery<GatewayRouteReturnByName<"chainwork.getLatestWorkflowRun">>("chainwork.getLatestWorkflowRun", {
      agentSessionId,
      workflowId: wf.id,
    });
  } catch {
    // Proceed with start (no run yet)
  }

  const isResume = run?.status === "active" && run.currentRoundId != null;
  const round = isResume
    ? wf.rounds.find((r) => r.id === run!.currentRoundId) ?? firstRound
    : firstRound;

  const skillHint =
    wf.id === "implementation-review"
      ? "Read the implementation-review skill (.cursor/skills/implementation-review/SKILL.md) for full facilitation instructions."
      : wf.id === "shape"
        ? "Read the shaping skill (.cursor/skills/shaping/SKILL.md) for full facilitation instructions."
        : null;

  const nextStep =
    `When this round is complete, call \`workflows\` with \`action=checkpoint\`, \`workflowId="${wf.id}"\`, \`roundId="${round.id}"\`, and \`output\` set to the round's synthesized output.` +
    (run?.runId ? ` Pass \`runId="${run.runId}"\` to checkpoint into the existing run.` : "");

  const lines = [
    isResume ? `# Resume: ${wf.name} (\`${wf.id}\`)` : `# Start: ${wf.name} (\`${wf.id}\`)`,
    "",
    `**Round ${round.num} — ${round.label}** (\`${round.id}\`)`,
    "",
    round.instruction,
    "",
    "**Facilitator guidance:**",
    round.facilitatorGuidance,
    "",
    "**Next step:**",
    nextStep,
    ...(skillHint ? ["", skillHint] : []),
  ];

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      isResume
        ? `Resume workflow ${wf.id} at round ${round.num} (${round.label}).`
        : `Start workflow ${wf.id} — round ${round.num} (${round.label}).`,
      {
        workflowId: wf.id,
        workflowName: wf.name,
        runId: run?.runId ?? null,
        round: {
          id: round.id,
          num: round.num,
          label: round.label,
          instruction: round.instruction,
          facilitatorGuidance: round.facilitatorGuidance,
          outputFormat: round.outputSchema.format,
        },
        nextStep: {
          action: "checkpoint",
          workflowId: wf.id,
          roundId: round.id,
          ...(run?.runId ? { runId: run.runId } : {}),
        },
      },
      [
        {
          tool: "workflows",
          description: `Checkpoint round ${round.num} when complete`,
          parameters: {
            action: "checkpoint",
            workflowId: wf.id,
            roundId: round.id,
            ...(run?.runId ? { runId: run.runId } : {}),
          },
        },
      ],
    ),
  };
}

interface PersistedWorkflowRun {
  runId: string;
  // Matches the server's real shape (`Id<"agentSessions"> | undefined` — a workflow run
  // isn't always started by an agent session), not required (PR #395, Lane C S3b pass).
  agentSessionId: string | undefined;
  workflowId: string;
  workflowName: string;
  templateSlug: string;
  templateName: string;
  status: "active" | "completed";
  startedAt: string;
  completedAt: string | null;
  currentRoundId: string | null;
  checkpoints: Array<{
    roundId: string;
    output: WorkflowRunOutput;
    recordedAt: string;
  }>;
  summary: {
    docId: string | null;
    entryId: string;
    collection: string | null;
    capturedAt: string | null;
  } | null;
  projection: WorkflowRunRenderProjection;
}

export async function handleGetRun(
  runId?: string,
  workflowId?: string,
) {
  let run: PersistedWorkflowRun | null;
  if (runId) {
    run = await kernelQuery<GatewayRouteReturnByName<"chainwork.getWorkflowRun">>("chainwork.getWorkflowRun", {
      runId,
    });
  } else {
    requireActiveSession();

    const agentSessionId = getAgentSessionId();
    if (!agentSessionId) {
      return validationResult(
        "Active session required for workflow run inspection by workflowId.",
      );
    }

    run = await kernelQuery<GatewayRouteReturnByName<"chainwork.getLatestWorkflowRun">>("chainwork.getLatestWorkflowRun", {
      agentSessionId,
      workflowId,
    });
  }

  if (!run) {
    const target = runId
      ? `workflow run '${runId}'`
      : `workflow '${workflowId}' in the current session`;
    return validationResult(`No persisted run found for ${target}.`);
  }

  if (workflowId && run.workflowId !== workflowId) {
    return validationResult(
      `Workflow run '${run.runId}' belongs to workflow '${run.workflowId}', not '${workflowId}'.`,
    );
  }

  const stepLines = run.projection.steps.map((step) => {
    const status = step.status.toUpperCase();
    const outputSummary = step.output
      ? ` — ${workflowRunOutputToText(step.output)}`
      : "";
    return `- ${step.num}. ${step.label} [${status}]${outputSummary}`;
  });

  const lines = [
    `# Workflow Run: ${run.workflowName}`,
    "",
    `- Run ID: \`${run.runId}\``,
    `- Workflow ID: \`${run.workflowId}\``,
    `- Status: ${run.status}`,
    `- Progress: ${run.projection.completedCount}/${run.projection.totalCount}`,
    `- Current round: ${run.currentRoundId ? `\`${run.currentRoundId}\`` : "complete"}`,
    `- Renderable sections: ${Object.keys(run.projection.render.links).length}/${run.projection.render.sections.length}`,
    `- Started: ${run.startedAt}`,
    ...(run.completedAt ? [`- Completed: ${run.completedAt}`] : []),
    ...(run.summary
      ? [`- Summary entry: \`${run.summary.entryId}\``]
      : []),
    "",
    "## Steps",
    ...stepLines,
  ];

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Loaded workflow run ${run.runId} (${run.workflowName}).`,
      run,
      run.summary
        ? [
            {
              tool: "relations",
              description: "Discover connections for the summary entry",
              parameters: { action: "suggest", entryId: run.summary.entryId },
            },
          ]
        : undefined,
    ),
  };
}

export async function handleCheckpoint(
  workflowId: string, roundId: string, output: WorkflowCheckpointOutputInput,
  isFinal?: boolean, restart?: boolean, summaryName?: string, summaryDescription?: string,
  summaryEntryId?: string, runId?: string | null, ownerTeamEntryId?: string, ownerRoleEntryId?: string,
) {
  const wf = getWorkflow(workflowId);
  if (!wf) {
    return {
      content: [{
        type: "text" as const,
        text:
          `Workflow "${workflowId}" not found. ` +
          `Available: ${listWorkflows().map((w) => w.id).join(", ")}.\n\n` +
          `This checkpoint was NOT saved. Continue the conversation — the facilitator has the context.`,
      }],
      structuredContent: failure(
        "NOT_FOUND",
        `Workflow '${workflowId}' not found.`,
        "Use workflows action=list to see available workflows.",
        [{ tool: "workflows", description: "List workflows", parameters: { action: "list" } }],
      ),
    };
  }

  const round = wf.rounds.find((r) => r.id === roundId);
  if (!round) {
    const availableRounds = wf.rounds.map((r) => r.id).join(", ");
    const closest = findClosestRound(roundId, wf.rounds);

    const correctionActions: NextAction[] = closest
      ? [
          {
            tool: "workflows",
            description: `Retry with corrected round ID '${closest.id}'`,
            parameters: { action: "checkpoint", workflowId, roundId: closest.id },
          },
        ]
      : [
          {
            tool: "workflows",
            description: "List workflows to inspect round IDs",
            parameters: { action: "list" },
          },
        ];

    const suggestionHint = closest ? ` Did you mean '${closest.id}'?` : "";

    return {
      content: [{
        type: "text" as const,
        text:
          `Round "${roundId}" not found in workflow "${workflowId}".${suggestionHint} ` +
          `Available rounds: ${availableRounds}.\n\n` +
          `This checkpoint was NOT saved. The conversation context is preserved — continue facilitating.`,
      }],
      structuredContent: failure(
        "NOT_FOUND",
        `Round '${roundId}' not found in workflow '${workflowId}'.`,
        closest
          ? `Did you mean '${closest.id}'? Available rounds: ${availableRounds}`
          : `Available rounds: ${availableRounds}`,
        correctionActions,
      ),
    };
  }

  const lines: string[] = [
    `## Checkpoint: Round ${round.num} — ${round.label}`,
    `Workflow: ${wf.name} (\`${wf.id}\`)`,
    "",
  ];

  if (restart && isFinal) {
    return validationResult(
      "restart cannot be combined with isFinal. Restart from the first round, then continue the workflow normally.",
    );
  }

  let normalizedOutput: WorkflowRunOutput;
  try {
    normalizedOutput = coerceWorkflowRunOutput(round, output);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    return validationResult(message);
  }

  const outputPreview = workflowRunOutputToText(normalizedOutput);

  if (isFinal) {
    const finalCheckpointValidationError = validateFinalWorkflowCheckpoint(
      wf,
      roundId,
    );
    if (finalCheckpointValidationError) {
      return validationResult(finalCheckpointValidationError);
    }

    const descriptor = getWorkflowDescriptor(workflowId);
    const template = descriptor
      ? getWorkflowTemplateMetadata(descriptor)
      : { slug: wf.id, name: wf.name };
    const summaryCapture = descriptor
      ? getWorkflowSummaryCaptureDefinition(descriptor)
      : wf.kbOutputCollection && wf.kbOutputTemplate
        ? {
            routing: {
              mode: "fixed" as const,
              collection: wf.kbOutputCollection,
            },
            nameTemplate: wf.kbOutputTemplate.nameTemplate,
            descriptionField: wf.kbOutputTemplate.descriptionField,
            descriptionSource: "final-output-text" as const,
          }
        : null;

    if (!summaryCapture && !summaryEntryId) {
      return validationResult(
        `Workflow '${workflowId}' does not support final summary capture through workflows.checkpoint. Follow the workflow guidance and use capture directly for its primary record, or pass summaryEntryId to link an existing entry.`,
      );
    }

    requireWriteAccess();
    const agentSessionId = getAgentSessionId();
    if (!agentSessionId) {
      return validationResult(
        "Active agent session required for workflow checkpoint persistence.",
      );
    }

    if (summaryEntryId && !summaryCapture) {
      return handleFacilitatedFinalization(
        wf, descriptor, roundId, normalizedOutput, agentSessionId, summaryEntryId, lines, runId,
      );
    }

    // After the guards above, summaryCapture is guaranteed non-null:
    // - Line 523: both null → early return
    // - Line 537: summaryEntryId truthy + summaryCapture null → early return
    const resolvedSummaryCapture = summaryCapture!;


    const existingRun = await kernelQuery<GatewayRouteReturnByName<"chainwork.getLatestWorkflowRun">>(
      "chainwork.getLatestWorkflowRun",
      {
        agentSessionId,
        workflowId: wf.id,
      },
    );
    if (!existingRun) {
      return validationResult(
        `Workflow '${workflowId}' has no durable run to finalize yet. Record checkpoints for the earlier rounds first.`,
      );
    }

    try {
      // FEAT-157: Pre-resolve collection for classifier-mode workflows (two-call pattern, DEC-163).
      // The Convex mutation can't call the LLM action, so MCP orchestrates.
      let resolvedCollectionSlug: string | undefined;
      if (resolvedSummaryCapture.routing.mode === "classifier") {
        const classifyName = summaryName ?? wf.name;
        const classifyDesc = summaryDescription ?? outputPreview;
        const resolved = await resolveCollection({ name: classifyName, description: classifyDesc });
        if (resolved && resolved.tier !== "low") {
          resolvedCollectionSlug = resolved.collection;
        }
      }

      const result = await kernelMutation<{
        runId: string;
        summary: {
          docId: string;
          entryId: string;
          name: string;
          created: boolean;
          collection: string | null;
        };
      }>("chainwork.finalizeWorkflowRun", {
        agentSessionId,
        workflowId: wf.id,
        workflowName: wf.name,
        templateSlug: template.slug,
        templateName: template.name,
        steps: wf.rounds.map((workflowRound) => ({
          roundId: workflowRound.id,
          num: workflowRound.num,
          label: workflowRound.label,
          type: workflowRound.type,
          outputField: workflowRound.outputSchema.field,
          outputFormat: workflowRound.outputSchema.format,
        })),
        finalRoundId: roundId,
        finalOutput: normalizedOutput,
        summaryCapture: {
          routing:
            resolvedSummaryCapture.routing.mode === "fixed"
              ? {
                  mode: "fixed" as const,
                  collection: resolvedSummaryCapture.routing.collection,
                }
              : {
                  mode: "classifier" as const,
                },
          nameTemplate: resolvedSummaryCapture.nameTemplate,
          descriptionField: resolvedSummaryCapture.descriptionField,
          descriptionSource: resolvedSummaryCapture.descriptionSource,
        },
        summaryDescription,
        summaryName,
        resolvedCollectionSlug,
        // WP-513 review round 3 (P1): rung-2 owner — no-op pre-rung-2.
        ...(ownerTeamEntryId ? { ownerTeamEntryId } : {}),
        ...(ownerRoleEntryId ? { ownerRoleEntryId } : {}),
      });

      lines.push(
        result.summary.created
          ? `**Draft Created**: \`${result.summary.entryId}\``
          : `**Draft Already Exists**: \`${result.summary.entryId}\``,
        `Collection: \`${result.summary.collection ?? (resolvedSummaryCapture.routing.mode === "fixed" ? resolvedSummaryCapture.routing.collection : "classifier-routed")}\``,
        `Name: ${result.summary.name}`,
        "",
        `The summary is captured as a draft. Use \`entries action=commit entryId="${result.summary.entryId}"\` to promote it to the Chain.`,
        `Use \`relations action=suggest entryId="${result.summary.entryId}"\` to discover connections.`,
      );

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: success(
          `Draft '${result.summary.name}' ${result.summary.created ? "created" : "reused"} (${result.summary.entryId}) for workflow ${workflowId}.`,
          {
            entryId: result.summary.entryId,
            workflowId,
            roundId,
            collection:
              result.summary.collection ??
              (resolvedSummaryCapture.routing.mode === "fixed"
                ? resolvedSummaryCapture.routing.collection
                : null),
            isFinal: true,
            runId: result.runId,
            created: result.summary.created,
          },
          [
            {
              tool: "entries",
              description: "Promote draft to Chain",
              parameters: { action: "commit", entryId: result.summary.entryId },
            },
            {
              tool: "relations",
              description: "Discover connections",
              parameters: { action: "suggest", entryId: result.summary.entryId },
            },
          ],
        ),
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      lines.push(
        `**Chain accept failed**: ${msg}`,
        "",
        `The workflow output is preserved in this conversation. `,
        `You can manually create the entry later using \`capture\` with:`,
        `- Collection: \`${resolvedSummaryCapture.routing.mode === "fixed" ? resolvedSummaryCapture.routing.collection : "classifier-routed"}\``,
        `- Name: ${summaryName ?? resolvedSummaryCapture.nameTemplate}`,
        `- Description: ${summaryDescription ?? outputPreview}`,
      );

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: failure(
          "BACKEND_ERROR",
          `Chain accept failed: ${msg}.`,
          "Capture manually with the capture tool.",
          [{ tool: "capture", description: "Capture manually", parameters: {} }],
        ),
      };
    }
  } else {
    requireWriteAccess();
    const agentSessionId = getAgentSessionId();
    if (!agentSessionId) {
      return validationResult(
        "Active agent session required for workflow checkpoint persistence.",
      );
    }

    try {
      const descriptor = getWorkflowDescriptor(workflowId);
      const template = descriptor
        ? getWorkflowTemplateMetadata(descriptor)
        : { slug: wf.id, name: wf.name };
      const checkpointResult = await kernelMutation<GatewayRouteReturnByName<"chainwork.recordWorkflowCheckpoint">>("chainwork.recordWorkflowCheckpoint", {
        agentSessionId,
        workflowId: wf.id,
        workflowName: wf.name,
        templateSlug: template.slug,
        templateName: template.name,
        steps: wf.rounds.map((workflowRound) => ({
          roundId: workflowRound.id,
          num: workflowRound.num,
          label: workflowRound.label,
          type: workflowRound.type,
          outputField: workflowRound.outputSchema.field,
          outputFormat: workflowRound.outputSchema.format,
        })),
        checkpoint: {
          roundId,
          output: normalizedOutput,
          recordedAt: new Date().toISOString(),
        },
        restart,
        ...(runId ? { runId } : {}),
        ...(summaryEntryId ? { summaryEntryId } : {}),
      });

      lines.push(
        `Round ${round.num} output recorded.`,
        `Output: ${outputPreview.substring(0, 200)}${outputPreview.length > 200 ? "..." : ""}`,
        `Progress: ${checkpointResult.projection.completedCount}/${checkpointResult.projection.totalCount} rounds completed.`,
      );

      const nextWorkflowRound = checkpointResult.projection.currentRoundId
        ? wf.rounds.find((workflowRound) => workflowRound.id === checkpointResult.projection.currentRoundId)
        : undefined;
      const nextRound = nextWorkflowRound
        ? { id: nextWorkflowRound.id, num: nextWorkflowRound.num, label: nextWorkflowRound.label }
        : undefined;

      if (checkpointResult.summary?.linked) {
        lines.push(
          "",
          `**Run completed.** Summary linked to existing entry \`${checkpointResult.summary.entryId}\`.`,
        );
      } else if (nextWorkflowRound) {
        lines.push(
          "",
          `**Next**: Round ${nextWorkflowRound.num} — ${nextWorkflowRound.label}`,
          `_${nextWorkflowRound.instruction}_`,
        );
      } else {
        lines.push(
          "",
          `**All rounds complete.** Call this tool again with \`isFinal: true\` to accept onto the Chain.`,
        );
      }

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: success(
          `Round ${round.num} (${round.label}) output recorded for workflow ${workflowId}.`,
          {
            workflowId,
            roundId,
            roundNum: round.num,
            roundLabel: round.label,
            isFinal: false,
            nextRound,
            runId: checkpointResult.runId,
            runStatus: checkpointResult.run.status,
            progress: {
              completedCount: checkpointResult.projection.completedCount,
              totalCount: checkpointResult.projection.totalCount,
            },
          },
        ),
      };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      lines.push(
        `**Checkpoint persistence failed**: ${msg}`,
        "",
        `The conversation context is preserved, but this round was not saved as a durable workflow checkpoint.`,
      );

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: failure(
          "BACKEND_ERROR",
          `Workflow checkpoint persistence failed: ${msg}.`,
          "Continue the workflow in conversation or retry the checkpoint.",
        ),
      };
    }
  }
}

async function handleFacilitatedFinalization(
  wf: WorkflowDefinition,
  descriptor: ReturnType<typeof getWorkflowDescriptor>,
  roundId: string,
  normalizedOutput: WorkflowRunOutput,
  agentSessionId: string,
  summaryEntryId: string,
  lines: string[],
  runId?: string | null,
) {
  const template = descriptor
    ? getWorkflowTemplateMetadata(descriptor)
    : { slug: wf.id, name: wf.name };

  try {
    const checkpointResult = await kernelMutation<GatewayRouteReturnByName<"chainwork.recordWorkflowCheckpoint">>("chainwork.recordWorkflowCheckpoint", {
      agentSessionId,
      workflowId: wf.id,
      workflowName: wf.name,
      templateSlug: template.slug,
      templateName: template.name,
      steps: wf.rounds.map((workflowRound) => ({
        roundId: workflowRound.id,
        num: workflowRound.num,
        label: workflowRound.label,
        type: workflowRound.type,
        outputField: workflowRound.outputSchema.field,
        outputFormat: workflowRound.outputSchema.format,
      })),
      checkpoint: {
        roundId,
        output: normalizedOutput,
        recordedAt: new Date().toISOString(),
      },
      summaryEntryId,
      ...(runId ? { runId } : {}),
    });

    const linked = checkpointResult.summary?.linked ?? false;
    lines.push(
      `**Run finalized**: Linked to existing entry \`${summaryEntryId}\`.`,
      `Status: ${checkpointResult.run.status}`,
      `Progress: ${checkpointResult.projection.completedCount}/${checkpointResult.projection.totalCount}`,
    );

    if (!linked && checkpointResult.run.status !== "completed") {
      lines.push(
        "",
        `Note: The run is not yet completed — the summary will be linked when all rounds are checkpointed.`,
      );
    }

    return {
      content: [{ type: "text" as const, text: lines.join("\n") }],
      structuredContent: success(
        `Workflow ${wf.id} finalized with linked entry ${summaryEntryId}.`,
        {
          entryId: summaryEntryId,
          workflowId: wf.id,
          roundId,
          isFinal: true,
          runId: checkpointResult.runId,
          linked,
          runStatus: checkpointResult.run.status,
        },
        [
          {
            tool: "relations",
            description: "Discover connections",
            parameters: { action: "suggest", entryId: summaryEntryId },
          },
        ],
      ),
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    lines.push(
      `**Finalization failed**: ${msg}`,
      "",
      `The workflow output is preserved in this conversation.`,
    );

    return {
      content: [{ type: "text" as const, text: lines.join("\n") }],
      structuredContent: failure(
        "BACKEND_ERROR",
        `Facilitated finalization failed: ${msg}.`,
        "The facilitate tool's state is preserved — retry the checkpoint or continue in conversation.",
      ),
    };
  }
}

export function coerceWorkflowRunOutput(
  round: WorkflowDefinition["rounds"][number],
  output: WorkflowCheckpointOutputInput,
): WorkflowRunOutput {
  if (typeof output !== "string") {
    assertWorkflowRunOutputMatchesRound(round, output);
    return output;
  }

  const trimmed = output.trim();

  switch (round.outputSchema.format) {
    case "freetext":
      return { format: "freetext", value: trimmed };
    case "list":
      return { format: "list", value: parseListOutput(trimmed) };
    case "choice":
      return { format: "choice", value: trimmed };
    case "structured":
      return { format: "structured", value: { text: trimmed } };
  }
}

function assertWorkflowRunOutputMatchesRound(
  round: WorkflowDefinition["rounds"][number],
  output: WorkflowRunOutput,
): void {
  if (output.format !== round.outputSchema.format) {
    throw new Error(
      `Round '${round.id}' expects '${round.outputSchema.format}' output, received '${output.format}'.`,
    );
  }
}

export function parseListOutput(output: string): string[] {
  const lines = output
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^[-*]\s+/, "").replace(/^\d+\.\s+/, "").trim())
    .filter(Boolean);

  return lines.length > 0 ? lines : [output];
}
